import { Injectable } from '@angular/core';
import { Instance } from '../models/instance';
import { ConfigSyncPlan, ConfigSyncRequest, ConfigSyncResult, ConfigSyncTarget } from '../models/config-sync';
import { Partner, PartnerBasicInfo } from '../models/partner';
import { SiteSection } from '../models/site-section';
import { Workspace } from '../models/workspace';
import { environment } from '../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class InstanceService {
  baseUrl = environment.apiEndpoint + '/instance/';

  siteSectionsFlat: SiteSection[] = [];
  siteSections: SiteSection[] = [];
  workspaces: Workspace[] = [];
  activeWorkspace: Workspace | null = null;
  siteSectionsLinks = [
    { type: 'ProjectList', url: '/projects' },
    { type: 'Team', url: '/team' },
    { type: 'Vendors', url: '/vendors' },
    { type: 'Clients', url: '/clients' },
    { type: 'ReportsCleanID', url: '/reports/cleanid/api' },
    { type: 'ReportsCleanIDNav', url: '/reports/cleanid/nav' },
    { type: 'TaskList', url: '/schedule' },
    { type: 'SystemAlerts', url: '/alerts' },
    { type: 'ReportsPerformance', url: '/reports/performance' },
    { type: 'Licensee', url: '/reports/licensee' },
    { type: 'ReportsAudit', url: '/reports/audit' },
    { type: 'ReportsReportBuilder', url: '/reports/builder' },
    { type: 'ReportsWorkload', url: '/reports/workload' },
    { type: 'ReportsClient', url: '/reports/client/invoice' },
    { type: 'ReportsClientColl', url: '/reports/client/coll' },
    { type: 'ReportsVendor', url: '/reports/vendor/invoiced' },
    { type: 'ReportsVendorClosed', url: '/reports/vendor/closed' },
    { type: 'ReportsResponseID', url: '/reports/responseid' },
    { type: 'ReportsResponseIDApi', url: '/reports/responseid/api' },
    { type: 'ReportsResponseIDNav', url: '/reports/responseid/nav' },
    { type: 'Dashboard', url: '/dashboard' },
    { type: 'Home', url: '/home' },
    { type: 'HomeManager', url: '/home/manager' },
    { type: 'Account', url: '/account' },
    { type: 'Bids', url: '/bids' },
    { type: 'BidSettings', url: '/bids/settings' },
    { type: 'BidDashboard', url: '/bids/dashboard' },
    { type: 'IDSuiteTest', url: '/idsuite-test' },
    { type: 'Programming', url: '/programming' },
    { type: 'QualityEvaluation', url: '/quality-evaluation' }
  ];

  constructor(private http: HttpClient) { }

  getInstances(): Observable<Instance[]> {
    return this.http.get<Instance[]>(this.baseUrl + '');
  }

  getInstance(id: any): Observable<Instance> {
    return this.http.get<Instance>(this.baseUrl + id);
  }

  addInstance(model: any): Observable<Instance> {
    return this.http.post<Instance>(this.baseUrl + '', model);
  }

  editInstance(model: any): Observable<Instance> {
    return this.http.put<Instance>(this.baseUrl + model.id, model);
  }

  deleteInstance(id: string) {
    return this.http.delete(this.baseUrl + id);
  }

  getUserSections(): Observable<SiteSection[]> {
    return this.http.get<SiteSection[]>(this.baseUrl + 'setup/userSections', { headers : {cache : '0'}});
  }

  getSections(instanceId): Observable<SiteSection[]> {
    return this.http.get<SiteSection[]>(this.baseUrl + 'setup/sections?instanceId='+ instanceId, { headers : {cache : '0'}});
  }

  addSection(model: any): Observable<SiteSection> {
    return this.http.post<SiteSection>(this.baseUrl + 'setup/sections', model);
  }

  editSection(model: any): Observable<SiteSection> {
    return this.http.put<SiteSection>(this.baseUrl + 'setup/sections/' + model.id, model);
  }

  deleteSection(id: any): Observable<SiteSection> {
    return this.http.delete<SiteSection>(this.baseUrl + 'setup/sections/' + id);
  }

  updateSectionRoles(sectionId: string, roleIds: string[]): Observable<any> {
    return this.http.put<any>(this.baseUrl + 'setup/sections/' + sectionId + '/roles', roleIds);
  }

  reorderSections(sections: any[]): Observable<any> {
    return this.http.put<any>(this.baseUrl + 'setup/sections/reorder', sections);
  }

  getUserWorkspaces(): Observable<Workspace[]> {
    return this.http.get<Workspace[]>(this.baseUrl + 'setup/userWorkspaces', { headers: { cache: '0' } });
  }

  getWorkspaces(instanceId: string): Observable<Workspace[]> {
    return this.http.get<Workspace[]>(this.baseUrl + 'setup/workspaces?instanceId=' + instanceId, { headers: { cache: '0' } });
  }

  addWorkspace(model: any): Observable<Workspace> {
    return this.http.post<Workspace>(this.baseUrl + 'setup/workspaces', model);
  }

  editWorkspace(model: any): Observable<Workspace> {
    return this.http.put<Workspace>(this.baseUrl + 'setup/workspaces/' + model.id, model);
  }

  deleteWorkspace(id: string): Observable<any> {
    return this.http.delete(this.baseUrl + 'setup/workspaces/' + id);
  }

  setActiveWorkspace(workspace: Workspace, instanceId: string) {
    this.activeWorkspace = workspace;
    if (workspace) {
      localStorage.setItem('activeWorkspace_' + instanceId, workspace.id);
    }
  }

  UpdateInstanceValue(instanceId: string, fieldId: string, value: any): Observable<any> {
    let field: any = {fieldId};
    if (typeof value === 'boolean') {
      field.bVal = value;
    }
    else if (typeof value === 'string') {
      field.sVal = value;
    }
    else if (typeof value === 'number') {
      field.nVal = value;
    }
    else if (value instanceof Date) {
      field.dVal = value;
    }
    return this.http.put<any>(this.baseUrl + instanceId + '/field', field );
  }
  SetupSiteSections(data, workspaceId?: string) {

    this.siteSectionsFlat = [];
    let sections = [];

    // Filter by workspace if specified
    if (workspaceId) {
      data = data.filter(item => item.workspaceId === workspaceId || item.workspaceId == null);
    }

    data.forEach(item => {
      item.children = [];
      this.stampSectionUrl(item);
      this.siteSectionsFlat.push(item);
      if (item.parentId == null)
        sections.push(item);
    });

    sections.forEach(item => {
      item.children = data.filter(x => x.parentId == item.id);
    });
    this.siteSections = sections;
  }

  private stampSectionUrl(item: SiteSection) {
    if (item.type == 'Partners') item.type = 'Vendors';
    item.icon = (item?.icon?.trim() == '' ? null : item.icon);

    if (item.type) {
      item.url = this.siteSectionsLinks.find(x => x.type == item.type)?.url;

      if (item.projectTypeId !== null && item.type === 'ProjectList') {
        item.url = item.url + '/list/' + item.projectTypeId;
      }
      if (item.otherInstanceId !== null && (item.type === 'ReportsResponseIDApi' || item.type === 'ReportsCleanID')) {
        item.url = item.url + '/' + item.otherInstanceId;
      }
      item.icon = (item.icon ? item.icon : 'fak fa-' + (item?.type.toLowerCase()) + ' fa-md');
      if ((item.type === 'ReportsVendor' || item.type === 'ReportsVendorClosed') && item.icon == 'fak fa-reportscendor fa-md') {
        item.icon += ' fa-xl'
      }
    } else {
      item.icon = (item.icon ? item.icon : 'fak fa-' + (item?.label.replace(' ', '').toLowerCase()) + ' fa-md');
    }
  }

  resolveWorkspaceForUrl(
    url: string,
    allSections: SiteSection[],
    userWorkspaces: Workspace[]
  ): { status: 'global' } | { status: 'inWorkspace'; workspace: Workspace } | { status: 'noAccess' } {
    if (!url || !allSections?.length) return { status: 'global' };

    // Normalize: strip query/hash, lower-case
    const cleanUrl = url.split('?')[0].split('#')[0].toLowerCase();
    if (!cleanUrl || cleanUrl === '/') return { status: 'global' };

    // Match against the *base* URL from siteSectionsLinks (looked up by type),
    // not the stamped URL — stamped URLs carry projectTypeId / otherInstanceId
    // suffixes that real deep-link routes (e.g. /projects/<id>/homepage) lack.
    const candidates = allSections
      .map(s => {
        const type = s.type === 'Partners' ? 'Vendors' : s.type;
        const baseUrl = this.siteSectionsLinks.find(x => x.type === type)?.url?.toLowerCase();
        return { section: s, baseUrl };
      })
      .filter(x => !!x.baseUrl && (cleanUrl === x.baseUrl || cleanUrl.startsWith(x.baseUrl + '/')));

    if (candidates.length === 0) return { status: 'global' };

    // Longest base URL wins so /reports/cleanid/api beats /reports
    const longest = Math.max(...candidates.map(x => x.baseUrl!.length));
    const bestMatches = candidates.filter(x => x.baseUrl!.length === longest);

    // Prefer a match in the currently-active workspace if one exists,
    // otherwise pick the lowest sortOrder.
    const preferred =
      bestMatches.find(x => x.section.workspaceId === this.activeWorkspace?.id) ??
      bestMatches.slice().sort((a, b) => (a.section.sortOrder ?? 0) - (b.section.sortOrder ?? 0))[0];

    if (preferred.section.workspaceId == null) return { status: 'global' };

    const workspace = userWorkspaces?.find(w => w.id === preferred.section.workspaceId);
    if (workspace) return { status: 'inWorkspace', workspace };

    return { status: 'noAccess' };
  }

  GetCurrentSection() {
    if(this.siteSectionsFlat == null || this.siteSectionsFlat.length == 0) return null;

    const url = window.location.pathname.toLowerCase();
    const path = '/' + (url.split('/')[1] ?? '');   // first path segment, e.g. "/bids"

    // Resolve most-specific first: exact full URL (multi-segment sections), then the exact first
    // segment (the common top-nav case), then the longest section URL that prefixes deeper routes.
    const currentSection =
      this.siteSectionsFlat.find(x => x.url?.toLowerCase() === url) ??
      this.siteSectionsFlat.find(x => x.url?.toLowerCase() === path) ??
      this.siteSectionsFlat
        .filter(x => x.url && url.startsWith(x.url.toLowerCase() + '/'))
        .sort((a, b) => (b.url?.length ?? 0) - (a.url?.length ?? 0))[0] ??
      null;

    if (currentSection?.parentId !== null) {
      let parent = this.siteSectionsFlat.find(x => x.id === currentSection?.parentId);
      return  {label: currentSection?.label, url:currentSection?.url, icon:currentSection?.icon, siteLabel: parent?.label};
    }
    return  {label: currentSection?.label, url:currentSection?.url, icon:currentSection?.icon, siteLabel: currentSection?.label};
  }

  getSyntheticPanel(): Observable<Array<PartnerBasicInfo>> {
    return this.http.get<Array<PartnerBasicInfo>>(this.baseUrl + 'syntheticPanel');
  }

  // Instance config sync. Targets come from the per-environment allowlist; an empty list means sync is disabled.
  getConfigSyncTargets(): Observable<ConfigSyncTarget[]> {
    return this.http.get<ConfigSyncTarget[]>(this.baseUrl + 'config-sync/targets', { headers: { cache: '0' } });
  }

  previewConfigSync(targetInstanceId: string, request: ConfigSyncRequest): Observable<ConfigSyncPlan> {
    return this.http.post<ConfigSyncPlan>(this.baseUrl + targetInstanceId + '/config-sync/preview', request);
  }

  applyConfigSync(targetInstanceId: string, request: ConfigSyncRequest): Observable<ConfigSyncResult> {
    return this.http.post<ConfigSyncResult>(this.baseUrl + targetInstanceId + '/config-sync/apply', request);
  }

}
